/**
 * FO-378 (batch-44) - app-breed versiebeleid per documentsoort.
 *
 * Eén beleid, drie klassen, vastgelegd als data (geen verspreide if-jes):
 *  - VRIJ:     bewerken/vervangen altijd; elke inhoudswijziging = versie +1
 *              (bestand vervangen, hergenereren of editor-opslag).
 *  - VIA_FLOW: wijzigen kan altijd, maar loopt via de eigen flow die de nieuwe
 *              versie maakt en de gevolgen regelt (akkoord/handtekening opnieuw).
 *  - NOOIT:    correctie alleen in de bronmodule; het document volgt vanzelf
 *              (loonstroken/jaaropgaven via payroll, facturen via facturatie,
 *              gegenereerde exports via de export zelf).
 *
 * De tabel sleutelt op het bibliotheek-documenttype (`DocumentType`, FO-064);
 * `bepaalDocumentVersieKlasse` legt daar de categorie-/scope-vangnetten
 * overheen voor documenten zonder type. De API-guard verfijnt VIA_FLOW verder
 * op de aanwezige flow-koppeling (gespreksverslag, arbeidsovereenkomst, ...):
 * een LOSSE upload van het type ARBEIDSOVEREENKOMST (extern getekende scan)
 * gedraagt zich als VRIJ - de flow-plicht geldt voor documenten die een flow
 * ALS BRON hebben. Zie `apps/api/src/lib/document-versiebeleid.ts`.
 *
 * Kernregel (spec 81 §3): een vastgelegd document wordt nooit gewijzigd - er
 * komt een nieuwe versie naast, met het oude exemplaar onaangetast in de
 * historie. Nooit stil overschrijven, nooit onnodig op slot.
 */
import type { DocumentType, DocumentCategorie, DocumentScope } from './documenten'

export const DOCUMENT_VERSIE_KLASSEN = ['VRIJ', 'VIA_FLOW', 'NOOIT'] as const
export type DocumentVersieKlasse = (typeof DOCUMENT_VERSIE_KLASSEN)[number]

export const DOCUMENT_VERSIE_KLASSE_LABELS: Record<DocumentVersieKlasse, string> = {
  VRIJ: 'Vrij te versioneren',
  VIA_FLOW: 'Nieuwe versie via de eigen flow',
  NOOIT: 'Correctie via de bronmodule',
}

/**
 * De klasse-tabel per bibliotheek-documenttype (spec 81 §3.2). Elke waarde van
 * `DocumentType` heeft een expliciete klasse zodat een nieuw type een bewuste
 * keuze afdwingt (TypeScript-volledigheid via het Record-type).
 */
export const DOCUMENT_VERSIEBELEID: Record<DocumentType, DocumentVersieKlasse> = {
  // NOOIT - de bron is een andere module; het register volgt vanzelf.
  FACTUUR: 'NOOIT',
  INKOOPFACTUUR: 'NOOIT',
  CREDITFACTUUR: 'NOOIT',
  LOONSTROOK: 'NOOIT',
  JAAROPGAVE: 'NOOIT',
  WERKBON: 'NOOIT',

  // VIA_FLOW - wijzigen kan, via de flow die de gevolgen regelt.
  ARBEIDSOVEREENKOMST: 'VIA_FLOW',
  WERKGEVERSVERKLARING: 'VIA_FLOW',
  KLANTCONTRACT: 'VIA_FLOW',
  VEILIGHEIDSDOCUMENT: 'VIA_FLOW',

  // VRIJ - bibliotheek-/dossier-documenten: nieuwe versie altijd mogelijk.
  CERTIFICAAT: 'VRIJ',
  DIPLOMA: 'VRIJ',
  SKILLMATRIX: 'VRIJ',
  BRUIKLEENOVEREENKOMST: 'VRIJ',
  DECLARATIEBON: 'VRIJ',
  OFFERTE: 'VRIJ',
  OPLEIDINGSDOCUMENT: 'VRIJ',
  HANDLEIDING: 'VRIJ',
  TEMPLATE: 'VRIJ',
  ALGEMEEN_DOCUMENT: 'VRIJ',
  SCHADEFORMULIER: 'VRIJ',
  ID_KAART: 'VRIJ',
  PASPOORT: 'VRIJ',
  RIJBEWIJS: 'VRIJ',
  BANKPAS: 'VRIJ',
  // Voertuigdocumenten: een verlengde groene kaart of nieuwe inzetverklaring
  // is een nieuwe versie van hetzelfde document (oude blijft in de historie).
  GROENE_KAART: 'VRIJ',
  INZETVERKLARING: 'VRIJ',
  // batch-65 apk-rapport (FO-478) - een betere scan van hetzelfde rapport is een
  // nieuwe versie; een NIEUWE keuring is een nieuw document (eigen logregel).
  APK_RAPPORT: 'VRIJ',
  // batch-67 (FO-501) - een nieuwe verklaring per jaar is een nieuw document.
  VERKLARING_PRIVEGEBRUIK: 'VRIJ',
  // batch-68 werkplaatsfacturen (FO-341) - spiegel van de inkoopfactuur-bijlage
  // (zelfde opslag-key): corrigeren via de inkoopfactuur, nooit een eigen versie.
  WERKPLAATSFACTUUR: 'NOOIT',
  // batch-71 workflow-sjablonen (FO-533 §D9) - gewone geuploade voertuigdocumenten.
  VERKOOPFACTUUR: 'VRIJ',
  VRIJWARINGSBEWIJS: 'VRIJ',
  KENTEKENBEWIJS: 'VRIJ',
  // batch-71 profiel-in-beeld (FO-538) - een nieuwe generatie is een nieuwe versie van
  // hetzelfde document; ook een handmatige nieuwe versie kan geen kwaad.
  PROFIELSCHETS: 'VRIJ',
}

/**
 * Categorie-vangnet voor documenten zonder bibliotheek-documenttype (legacy:
 * `documenttype` is nullable). Alleen de categorieën met een afwijkende klasse
 * staan hier; de rest valt op VRIJ.
 */
const CATEGORIE_VERSIEBELEID: Partial<Record<DocumentCategorie | 'LOONSTROOK' | 'JAAROPGAVE', DocumentVersieKlasse>> = {
  LOONSTROOK: 'NOOIT',
  JAAROPGAVE: 'NOOIT',
  CONTRACT: 'VIA_FLOW',
  // Gespreksverslagen (EVALUATIE) lopen via de verslag-editor; de API-guard
  // herkent het gegenereerde verslag óók aan de 1:1-koppeling.
  EVALUATIE: 'VIA_FLOW',
}

/**
 * Bepaal de versie-klasse van een document. Volgorde:
 *  1. scope EXPORT -> NOOIT (gegenereerde exports; draai de export opnieuw);
 *  2. het bibliotheek-documenttype (de tabel hierboven);
 *  3. het categorie-vangnet (legacy zonder type);
 *  4. anders VRIJ (dossier-uploads).
 */
export function bepaalDocumentVersieKlasse(input: {
  documenttype?: DocumentType | null
  categorie?: DocumentCategorie | string | null
  scope?: DocumentScope | string | null
}): DocumentVersieKlasse {
  if (input.scope === 'EXPORT') return 'NOOIT'
  if (input.documenttype) return DOCUMENT_VERSIEBELEID[input.documenttype]
  if (input.categorie) {
    const vanCategorie =
      CATEGORIE_VERSIEBELEID[input.categorie as keyof typeof CATEGORIE_VERSIEBELEID]
    if (vanCategorie) return vanCategorie
  }
  return 'VRIJ'
}

// ─── Centrale copy-helper (spec 81 §10.4) ────────────────────────────────────
//
// De "bevroren"-meldingen stonden verspreid (verslag-editor, document-detail,
// contract-tab) en eindigden in een doodlopend "kan niet". Eén helper levert
// per situatie de consistente melding + het handelingsperspectief, voor de API
// (409-messages) én de frontends (banners/knoppen). STREEP-stijl, geen em-dash.

/** De bron-flows die een VIA_FLOW-document kunnen dragen. */
export type DocumentVersieFlow =
  | 'GESPREKSVERSLAG'
  | 'ARBEIDSOVEREENKOMST'
  | 'WERKGEVERSVERKLARING'
  | 'KLANTCONTRACT'
  | 'KLANTDOCUMENT'
  | 'NEN3140'
  | 'ONBOARDING_DOCUMENT'
  | 'RIE'
  | 'CHECKLIST'

/** De bronmodules voor NOOIT-documenten. */
export type DocumentVersieBronModule = 'PAYROLL' | 'FACTURATIE' | 'EXPORT' | 'WERKBON'

export interface VersiebeleidMelding {
  titel: string
  tekst: string
  /** Label voor de actie-knop/link als er een direct handelingsperspectief is. */
  actieLabel: string | null
}

const FLOW_MELDINGEN: Record<DocumentVersieFlow, VersiebeleidMelding> = {
  GESPREKSVERSLAG: {
    titel: 'Gegenereerd gespreksverslag',
    tekst:
      'Dit is een gegenereerd gespreksverslag: wijzigingen maak je in de verslag-editor. ' +
      'Na vrijgave of akkoord gebruik je daar "Nieuwe versie maken"; de vorige versie blijft in de historie.',
    actieLabel: 'Naar de verslag-editor',
  },
  ARBEIDSOVEREENKOMST: {
    titel: 'Arbeidsovereenkomst',
    tekst:
      'Een arbeidsovereenkomst wijzig je via de contracten-flow op het medewerkerdossier: ' +
      'een nieuwe versie start daar een nieuw tekentraject. Deze versie blijft in de historie.',
    actieLabel: 'Naar het contract',
  },
  WERKGEVERSVERKLARING: {
    titel: 'Werkgeversverklaring',
    tekst:
      'Een werkgeversverklaring wijzig je door een nieuwe generatie te starten; ' +
      'elke generatie is een nieuwe versie en eerdere versies blijven bewaard.',
    actieLabel: 'Naar de verklaring',
  },
  KLANTCONTRACT: {
    titel: 'Klantcontract',
    tekst:
      'Een klantcontract wijzig je via een nieuwe contractversie in de contracten-flow ' +
      'van de relatie; getekende versies blijven onaangetast in de historie.',
    actieLabel: 'Naar het klantcontract',
  },
  KLANTDOCUMENT: {
    titel: 'Gegenereerd klantdocument',
    tekst:
      'Dit document is gegenereerd uit een sjabloon: hergenereer het via de documenten-flow ' +
      'van de relatie of medewerker; elke hergeneratie is een nieuwe versie.',
    actieLabel: 'Opnieuw genereren',
  },
  NEN3140: {
    titel: 'NEN 3140-aanwijzing',
    tekst:
      'Een aanwijzing wijzig je via de NEN 3140-module (hergenereren); ' +
      'elke hergeneratie is een nieuwe versie van hetzelfde document.',
    actieLabel: 'Naar de aanwijzing',
  },
  ONBOARDING_DOCUMENT: {
    titel: 'Onboarding-document',
    tekst:
      'Dit document is gegenereerd in de onboarding-flow: hergenereer het daar; ' +
      'elke hergeneratie is een nieuwe versie van hetzelfde document.',
    actieLabel: 'Naar de onboarding',
  },
  RIE: {
    titel: 'RI&E-document',
    tekst: 'De RI&E-PDF volgt de RI&E-beoordeling; stel daar een nieuwe versie vast.',
    actieLabel: 'Naar de RI&E',
  },
  CHECKLIST: {
    titel: 'Controlelijst-PDF',
    tekst: 'Deze PDF volgt de ingevulde controlelijst; wijzig of hergenereer de invulling daar.',
    actieLabel: 'Naar de controlelijst',
  },
}

const BRON_MELDINGEN: Record<DocumentVersieBronModule, VersiebeleidMelding> = {
  PAYROLL: {
    titel: 'Loonstrook of jaaropgave',
    tekst:
      'Loonstroken en jaaropgaven zijn wettelijk bevroren in het register: ' +
      'een correctie loopt via payroll en het gecorrigeerde document volgt vanzelf.',
    actieLabel: 'Naar payroll',
  },
  FACTURATIE: {
    titel: 'Factuur',
    tekst:
      'Facturen wijzig je niet in het register: een correctie loopt via de facturatie ' +
      '(creditnota-regime) en het gecorrigeerde document volgt vanzelf.',
    actieLabel: 'Naar facturatie',
  },
  EXPORT: {
    titel: 'Gegenereerde export',
    tekst: 'Een bewaarde export wijzig je niet: draai de export opnieuw voor een actuele versie.',
    actieLabel: null,
  },
  WERKBON: {
    titel: 'Werkbon-PDF',
    tekst: 'De werkbon-PDF volgt de werkbon; een correctie loopt via de werkbonnen-module.',
    actieLabel: 'Naar de werkbon',
  },
}

/** Melding voor een VIA_FLOW-document met een bekende bron-flow. */
export function versieFlowMelding(flow: DocumentVersieFlow): VersiebeleidMelding {
  return FLOW_MELDINGEN[flow]
}

/** Melding voor een NOOIT-document (verwijzing naar de bronmodule). */
export function versieBronMelding(bron: DocumentVersieBronModule): VersiebeleidMelding {
  return BRON_MELDINGEN[bron]
}

// ─── Gespreksverslag-herziening (deel A) ─────────────────────────────────────

/** Copy voor de verslag-herzieningsflow (gedeeld backoffice/portal/API). */
export const VERSLAG_VERSIE_COPY = {
  nieuweVersieKnop: 'Nieuwe versie maken',
  terugNaarConceptKnop: 'Terug naar concept',
  /** {n} = het nieuwe versienummer. */
  aangepastWachtOpAkkoord: (n: number) => `Aangepast - versie ${n}, wacht op akkoord`,
  herzieningOpen:
    'Er staat een herziening open: rond die eerst af (vrijgeven) of werk daarin verder.',
  vrijgegevenBewerkenViaVersie:
    'Dit verslag is vrijgegeven. Wijzigen kan via "Nieuwe versie maken": ' +
    'de vastgelegde versie blijft met handtekeningen in de historie en de medewerker ' +
    'krijgt de nieuwe versie opnieuw ter akkoord.',
  definitiefTerugNaarConcept:
    'Het verslag is definitief maar nog niet verzonden. Zet het terug naar concept om ' +
    'de inhoud aan te passen; er is nog geen versie vastgelegd.',
} as const
